import { post, patch, put, del, get } from 'aws-amplify/api';
import { processResponse } from './api_util';
import useSWR, { mutate } from 'swr'
import { useContext } from 'react';
import UserProfileContext from '../context/userprofilecontext';

async function getCaseStats() {

  const payload = {
    apiName: 'LivisiblyCaseStatsApi',
    path: 'case_stats'

  }

  const restOperation = get(payload);


  const response = await restOperation.response;

  return processResponse(response)
}

async function getCachedCaseStats(caseId) {
  // add jitter to refresh interval to avoid thundering herd problem
  const jitter = Math.floor(Math.random() * 10000); // random jitter between 0 and 10 seconds

  //sleep for jitter duration
  await new Promise(resolve => setTimeout(resolve, jitter));

  const payload = {
    apiName: 'LivisiblyCaseStatsApi',
    path: `case_stats/case/${caseId}`

  }

  const restOperation = get(payload);


  const response = await restOperation.response;

  return processResponse(response)
}


export function useGetCaseStats(shouldFetch = true) {
  // Key by the active caseID so switching cases cache-misses instead of
  // showing stale stats from the previously-selected case. The backend
  // endpoint itself is still scoped to the user's current case server-side;
  // the caseID in the key is only used for client cache partitioning.
  // NOTE: this 'CASE_STATS' namespace must stay distinct from the cached
  // per-case hook below (useGetCachedCaseStats → 'CACHED_CASE_STATS'). They
  // key by the same caseID but return incompatible shapes — see that hook.
  const { userProfile } = useContext(UserProfileContext);
  const caseID = userProfile?.currentCase?.caseID || null;
  const result = useSWR(
    shouldFetch && caseID ? ['CASE_STATS', caseID] : null,
    () => getCaseStats(),
    { refreshInterval: 7000 } // Refresh every 7 seconds
  );
  return result;
}

// Instant cached-first read for the CURRENT user's case. Hits /case_stats/cached,
// a primary-DB-only endpoint that returns the last daily :CaseStats snapshot and
// NEVER wakes/queries the case Neo4j DB — so it comes back fast even when the case
// DB has just spun up. The live /case_stats call (below) fans out ~16 serial
// Cypher queries against that freshly-woken DB and is the slow path; this lets the
// dashboard + nav paint last-known data immediately while the live call catches up.
async function getInstantCachedCaseStats() {
  const payload = {
    apiName: 'LivisiblyCaseStatsApi',
    path: 'case_stats/cached',
  };
  const restOperation = get(payload);
  const response = await restOperation.response;
  return processResponse(response);
}

export function useGetInstantCachedCaseStats(shouldFetch = true) {
  // Distinct key namespace from BOTH useGetCaseStats ('CASE_STATS') and
  // useGetCachedCaseStats ('CACHED_CASE_STATS'): this endpoint returns the
  // {stats, cached} envelope like the latter but is keyed by the caller's current
  // case (not an explicit caseId) and never wakes the DB. Fetch once — the live
  // hook owns freshness — so no refreshInterval and no revalidate-on-focus churn.
  const { userProfile } = useContext(UserProfileContext);
  const caseID = userProfile?.currentCase?.caseID || null;
  const result = useSWR(
    shouldFetch && caseID ? ['CASE_STATS_CACHED_INSTANT', caseID] : null,
    () => getInstantCachedCaseStats(),
    { revalidateOnFocus: false, revalidateIfStale: false, revalidateOnReconnect: false }
  );
  return result;
}

// Cached-first dashboard stats: prefer the live /case_stats result, but fall back
// to the instant cached snapshot until the live call lands. Both AppShell (nav
// gating) and HomeDashboard consume this, and SWR dedupes the shared keys so there
// is exactly one live fetch and one cached fetch regardless of how many components
// call it. `isCachedOnly` lets callers show a subtle "updating…" affordance.
export function useDashboardCaseStats(shouldFetch = true) {
  const live = useGetCaseStats(shouldFetch);
  // Stop the cached fetch the moment live data exists — from then on live owns the
  // display and the cached key goes inert (its last value stays in cache harmlessly).
  const cached = useGetInstantCachedCaseStats(shouldFetch && !live.data);
  const cachedStats = cached.data?.stats || null;
  const data = live.data || cachedStats || undefined;
  return {
    ...live,
    data,
    isCachedOnly: !live.data && !!cachedStats,
  };
}

export function useGetCachedCaseStats(caseId, shouldFetch = true) {
  // IMPORTANT: this MUST use a different key namespace than useGetCaseStats
  // above ('CACHED_CASE_STATS' vs 'CASE_STATS'). The two hooks key by caseID
  // but hit different endpoints with incompatible response shapes — the live
  // /case_stats returns a bare stats dict, while this cached
  // /case_stats/case/{id} returns the {stats, db_state, cached} envelope the
  // admin panel reads. Sharing the 'CASE_STATS' namespace made SWR dedupe them
  // to one cache entry whenever the admin shows the *currently-selected* case,
  // so the live dashboard fetcher clobbered the cache the admin row reads —
  // surfacing as 0/0 stats + "DB offline" for that one case. Keep distinct.
  const result = useSWR(
    shouldFetch && caseId ? ['CACHED_CASE_STATS', caseId] : null,
    () => getCachedCaseStats(caseId),
    { refreshInterval: 30000 } // Refresh every 30 seconds
  );
  return result;
}

// Drill-down resolver — given a stat key from the dashboard cards (e.g. the
// "N Transactions Flagged for Review" card), return the exact transaction IDs
// that the count is claiming. Mirrors useGetDRFADrilldownTransactionIDs in
// pattern; both feed the same TransactionAnalyzer destination via the
// transaction-viewing skill.
async function getCaseStatsDrilldownTransactionIDs(stat) {
  const queryString = new URLSearchParams({ stat }).toString();
  const payload = {
    apiName: 'LivisiblyCaseStatsApi',
    path: `case_stats/drilldown?${queryString}`,
  };
  const restOperation = get(payload);
  const response = await restOperation.response;
  return processResponse(response);
}

export function useGetCaseStatsDrilldownTransactionIDs(stat, shouldFetch = true) {
  const { userProfile } = useContext(UserProfileContext);
  const caseID = userProfile?.currentCase?.caseID || null;
  const result = useSWR(
    shouldFetch && caseID && stat
      ? ['CASE_STATS_DRILLDOWN', caseID, stat]
      : null,
    () => getCaseStatsDrilldownTransactionIDs(stat),
  );
  return result;
}


